import { ComponentProps } from 'react'

import { motion } from 'motion/react'
import { tv } from 'tailwind-variants'

import { Badge } from '@/components/ui/badge/badge'

import { Icon } from '../Icon'

export const filterChipVariants = tv({
  base: 'inline-flex h-auto cursor-default items-center !gap-2 !rounded-lg border-transparent',
  variants: {
    variant: {
      selected: 'bg-primary-400 text-surface-white-50'
    },
    size: {
      normal: '!px-2 !py-2',
      sm: '!px-2 !py-2',
      xs: '!px-2 !py-1'
    }
  },
  defaultVariants: {
    variant: 'selected',
    size: 'normal'
  }
})

const sizeConfig = {
  normal: { text: 'text-body-md font-normal' },
  sm: { text: 'text-caption-lg font-semibold' },
  xs: { text: 'text-caption-sm font-bold' }
} satisfies Record<string, { text: string }>

export const chipAnimationVariants = {
  initial: { scale: 0.8, opacity: 0 },
  animate: { scale: 1, opacity: 1, transition: { duration: 0.2 } },
  exit: { scale: 0.8, opacity: 0, transition: { duration: 0.15 } }
}

export type FilterChipSize = 'normal' | 'sm' | 'xs'

export interface FilterChipProps
  extends Omit<ComponentProps<typeof Badge>, 'children' | 'size'> {
  label: string
  onRemove: () => void
  layoutId?: string
  size?: FilterChipSize
  /** Enable enter/exit animations. Defaults to true. */
  animated?: boolean
}

export const FilterChip = ({
  label,
  onRemove,
  className,
  layoutId,
  size = 'normal',
  animated = true,
  ...props
}: FilterChipProps): JSX.Element => {
  const config = sizeConfig[size]

  const chip = (
    <Badge
      className={filterChipVariants({
        variant: 'selected',
        size,
        className: `${config.text} ${className ?? ''}`
      })}
      {...props}
    >
      <span>{label}</span>
      <button
        type='button'
        onClick={onRemove}
        className='cursor-pointer hover:opacity-80'
        aria-label={`Remove ${label} filter`}
      >
        <Icon name='close' size='inherit' />
      </button>
    </Badge>
  )

  if (!animated) {
    return chip
  }

  return (
    <motion.div
      layout
      layoutId={layoutId}
      variants={chipAnimationVariants}
      initial='initial'
      animate='animate'
      exit='exit'
    >
      {chip}
    </motion.div>
  )
}
